import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import SettingsIcon from "@mui/icons-material/Settings";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { exportSalesCsv, fetchSalesReport, fetchSalesReportReps } from "../../../../services/reports";
import { fetchTeams } from "../../../../services/teams";
import { useParams } from "react-router-dom";
import {
  UITableShell,
  UITableToolbar,
  UIStatusChip,
} from "../../../../components/ui";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  getIndustryLabel,
  getIndustryOptions,
  UNCLASSIFIED_INDUSTRY_LABEL,
  UNCLASSIFIED_INDUSTRY_VALUE,
} from "../../../../constants/industries";
import ReportSummaryCard from "../shared/ReportSummaryCard";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import { normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import {
  reportFilterCardContentSx,
  reportFilterGridSx,
  reportSummaryGridSx,
} from "../shared/reportLayout";

const currency = (n) => `CA$${(Number(n) || 0).toFixed(2)}`;
const percent = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const densitySettings = {
  compact: { py: 0.75, fontSize: "0.8125rem" },
  comfortable: { py: 1.4, fontSize: "0.875rem" },
  spacious: { py: 2.05, fontSize: "0.9375rem" },
};

const reportShellSx = {
  flex: 1,
  borderRadius: "10px",
  border: "1px solid",
  borderColor: "divider",
  borderTop: "3px solid",
  borderTopColor: "primary.main",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
};

const DEFAULT_SUMMARY = {
  totalSales: 0,
  totalOrders: 0,
  averageOrderSize: 0,
  totalMargin: 0,
  marginPct: 0,
};

const SALES_REPORT_TYPE_OPTIONS = [
  { value: "salesSummary", label: "Sales Summary" },
  { value: "salesToBudget", label: "Sales to Budget" },
  { value: "forecast", label: "Forecast" },
  { value: "weeklyPerformance", label: "Weekly Performance" },
  { value: "newVsRepeat", label: "New vs Repeat" },
  { value: "byCustomer", label: "By Customer" },
  { value: "byIndustry", label: "By Industry" },
  { value: "customerMarginByRep", label: "Customer Margin by Rep" },
  { value: "customerSalesHistory", label: "Customer Sales History" },
  { value: "averageOrderByCustomer", label: "Avg Order by Customer" },
];

const SALES_REPORT_TYPE_SET = new Set(SALES_REPORT_TYPE_OPTIONS.map((option) => option.value));
const SALES_GROUP_BY_SET = new Set(["none", "month", "quarter", "annual"]);
const SALES_VIEW_SET = new Set(["booked", "invoiced"]);
const ASSIGNMENT_FILTER_OPTIONS = [
  { value: "anyAssignment", label: "Any Assignment" },
  { value: "clientRep", label: "Client Rep Assignment" },
  { value: "orderRep", label: "Order Rep Assignment" },
];
const ASSIGNMENT_FILTER_SET = new Set(ASSIGNMENT_FILTER_OPTIONS.map((option) => option.value));
const PERIOD_PRESET_SET = new Set(["fiscalYear", "calendarYear", "custom"]);
const DEFAULT_SALES_FILTERS = {
  dateFrom: "",
  dateTo: "",
  week: "",
  groupBy: "none",
  salesView: "booked",
  industry: "",
  teamId: "",
  repId: "",
  quickFind: "",
  assignmentFilter: "anyAssignment",
  periodPreset: "fiscalYear",
  reportType: "salesSummary",
};

export const ordinal = (day) => {
  const mod10 = day % 10;
  const mod100 = day % 100;
  if (mod10 === 1 && mod100 !== 11) return "st";
  if (mod10 === 2 && mod100 !== 12) return "nd";
  if (mod10 === 3 && mod100 !== 13) return "rd";
  return "th";
};

export const friendlyDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const month = date.toLocaleString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  return `${month} ${day}${ordinal(day)}, ${year}`;
};

const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    return normalizeAvatarUrl(selectedPic?.url || null);
  }
  if (typeof imgSrc === "string") {
    if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
    if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
    if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      // Keep raw fallback below.
    }
  }
  return imgSrc;
};

const repDisplayName = (rep) => {
  const name = [rep?.first_name || rep?.firstName, rep?.last_name || rep?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || rep?.email || (rep?.id != null ? `User ${rep.id}` : "");
};

const formatRepNames = (reps, fallback = "—") => {
  const list = Array.isArray(reps) ? reps.filter(Boolean) : [];
  if (!list.length) return fallback || "—";
  const names = list.map(repDisplayName).filter(Boolean).join(", ");
  return names || fallback || "—";
};

const RepList = ({ reps, fallback = "—" }) => {
  const list = Array.isArray(reps) ? reps.filter(Boolean) : [];
  if (!list.length) return <span>{fallback || "—"}</span>;

  const allNames = formatRepNames(list, fallback);
  const initials = (name) =>
    String(name || "")
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }} title={allNames}>
      {list.slice(0, 3).map((rep, index) => {
        const name = repDisplayName(rep);
        return (
          <Avatar
            key={`${rep?.id || rep?.email || name || "rep"}-${index}`}
            src={normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined}
            alt={name}
            sx={{ width: 26, height: 26, fontSize: 12 }}
          >
            {initials(name)}
          </Avatar>
        );
      })}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {allNames}
      </Box>
      {list.length > 3 && <Chip size="small" variant="outlined" label={`+${list.length - 3}`} />}
    </Box>
  );
};

const RepSummaryValue = ({ rep, fallback = "All" }) => {
  if (!rep) return <span>{fallback}</span>;

  const name = repDisplayName(rep) || fallback;
  const src = normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined;
  const initials = String(name || "")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Avatar src={src} alt={name} sx={{ width: 28, height: 28, fontSize: 12 }}>
        {initials}
      </Avatar>
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {name}
      </Box>
    </Box>
  );
};

const normalizeSavedRepOption = (rep) => {
  if (!rep) return null;
  const id = rep?.id != null && String(rep.id).trim() !== "" ? String(rep.id) : "";
  const normalized = {
    id: id || null,
    first_name: rep?.first_name || rep?.firstName || "",
    last_name: rep?.last_name || rep?.lastName || "",
    firstName: rep?.firstName || rep?.first_name || "",
    lastName: rep?.lastName || rep?.last_name || "",
    email: rep?.email || "",
    profilePicture: rep?.profilePicture || null,
    avatar: rep?.avatar || null,
    photoUrl: rep?.photoUrl || null,
    role: rep?.role || "",
    roleLabel: rep?.roleLabel || "",
    label: rep?.label || "",
  };
  const fallbackLabel = repDisplayName(normalized);
  if (!normalized.id && !fallbackLabel) return null;
  normalized.label = normalized.label || fallbackLabel;
  return normalized;
};

const getColumnValue = (row, field) => {
  if (!row || !field) return undefined;
  return field.split(".").reduce((value, key) => value?.[key], row);
};

const getStatusTone = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["won", "ahead", "healthy", "on track", "strong", "available", "yes"].includes(normalized)) {
    return "success";
  }
  if (["lost", "behind", "at risk", "off track", "no"].includes(normalized)) {
    return "error";
  }
  if (["pending", "open", "in progress", "forecast", "missing budget"].includes(normalized)) {
    return "warning";
  }
  return "neutral";
};

const renderStatusValue = (value, fallback = "—") => {
  if (value == null || value === "") return fallback;
  return <UIStatusChip label={String(value)} tone={getStatusTone(value)} />;
};

const getInvoicePaymentRowAccentSx = (row = {}) => {
  const status = String(row?.invoicePaymentStatus || "").toLowerCase();

  if (status === "paid") {
    return {
      boxShadow: "inset 4px 0 0 var(--ec-status-success-border, #22c55e)",
      backgroundColor: "rgba(34, 197, 94, 0.04)",
      "& .MuiTableCell-root": {
        backgroundColor: "rgba(34, 197, 94, 0.04)",
      },
      "&:hover": {
        backgroundColor: "rgba(34, 197, 94, 0.08)",
        "& .MuiTableCell-root": {
          backgroundColor: "rgba(34, 197, 94, 0.08)",
        },
      },
    };
  }

  if (status === "partial") {
    return {
      boxShadow: "inset 4px 0 0 var(--ec-status-warning-border, #f59e0b)",
      backgroundColor: "rgba(245, 158, 11, 0.05)",
      "& .MuiTableCell-root": {
        backgroundColor: "rgba(245, 158, 11, 0.05)",
      },
      "&:hover": {
        backgroundColor: "rgba(245, 158, 11, 0.09)",
        "& .MuiTableCell-root": {
          backgroundColor: "rgba(245, 158, 11, 0.09)",
        },
      },
    };
  }

  if (status === "unpaid") {
    return {
      boxShadow: "inset 4px 0 0 var(--ec-color-border-strong, #cbd5e1)",
      backgroundColor: "rgba(148, 163, 184, 0.04)",
      "& .MuiTableCell-root": {
        backgroundColor: "rgba(148, 163, 184, 0.04)",
      },
      "&:hover": {
        backgroundColor: "rgba(148, 163, 184, 0.08)",
        "& .MuiTableCell-root": {
          backgroundColor: "rgba(148, 163, 184, 0.08)",
        },
      },
    };
  }

  return {};
};

const compareValues = (left, right, sortType = "string") => {
  const aMissing = left == null || left === "";
  const bMissing = right == null || right === "";
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (sortType === "number" || sortType === "currency" || sortType === "percent") {
    return Number(left || 0) - Number(right || 0);
  }

  if (sortType === "date") {
    return new Date(left).getTime() - new Date(right).getTime();
  }

  if (sortType === "boolean") {
    return Number(Boolean(left)) - Number(Boolean(right));
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const getSortedRows = (rows, columns, sortBy, sortDir) => {
  if (!sortBy) return rows;
  const activeColumn = columns.find((column) => (column.field || column.headerName) === sortBy);
  if (!activeColumn) return rows;

  return [...rows].sort((leftRow, rightRow) => {
    const leftValue = activeColumn.sortValue
      ? activeColumn.sortValue({
          row: leftRow,
          value: getColumnValue(leftRow, activeColumn.field),
        })
      : getColumnValue(leftRow, activeColumn.field);
    const rightValue = activeColumn.sortValue
      ? activeColumn.sortValue({
          row: rightRow,
          value: getColumnValue(rightRow, activeColumn.field),
        })
      : getColumnValue(rightRow, activeColumn.field);

    const comparison = compareValues(leftValue, rightValue, activeColumn.sortType);
    return sortDir === "asc" ? comparison : comparison * -1;
  });
};

const ReportTableContent = ({
  rows = [],
  columns = [],
  loading = false,
  emptyMessage = "No data available.",
  page = 0,
  rowsPerPage = 25,
  sortBy = "",
  sortDir = "asc",
  onSort,
  onPageChange,
  onRowsPerPageChange,
  density = "comfortable",
  getRowSx,
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];
  const pagedRows = safeRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalVisibleColumns = safeColumns.length || 1;
  const minWidth = safeColumns.reduce((sum, column) => sum + (column.minWidth || 140), 0);

  return (
    <>
      <TableContainer
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--ec-color-border-strong, #cbd5e1)",
            borderRadius: 99,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth, width: "100%" }}>
          <TableHead>
            <TableRow>
              {safeColumns.map((column) => {
                const columnKey = column.field || column.headerName;
                const sortable = column.sortable !== false;
                const isSorted = sortBy === columnKey;

                return (
                  <TableCell
                    key={columnKey}
                    onClick={() => sortable && onSort(columnKey)}
                    sx={{
                      minWidth: column.minWidth || 120,
                      px: 2,
                      py: 1.25,
                      backgroundColor: "#f8fafc !important",
                      color: isSorted
                        ? "var(--ec-table-head-text-inverse, #0f172a)"
                        : "var(--ec-table-head-text-muted, #475569)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      borderBottom: "none",
                      cursor: sortable ? "pointer" : "default",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      transition: "all 0.15s ease",
                      "&:hover": sortable
                        ? {
                            backgroundColor: "var(--ec-table-head-bg-hover, #eef2f7) !important",
                            color: "var(--ec-table-head-text-inverse, #0f172a)",
                          }
                        : undefined,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {column.headerName || column.field}
                      {sortable ? (
                        <Box sx={{ display: "inline-flex", opacity: isSorted ? 1 : 0.45 }}>
                          {isSorted ? (
                            sortDir === "asc" ? (
                              <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                            ) : (
                              <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                            )
                          ) : (
                            <UnfoldMoreIcon sx={{ fontSize: 13 }} />
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              "& .MuiTableCell-root": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                borderBottom: "none",
              },
            }}
          >
            {loading
              ? [...Array(8)].map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {safeColumns.map((column) => (
                      <TableCell
                        key={`${column.field || column.headerName}-skeleton-${rowIndex}`}
                        sx={{ px: 2, py: densitySettings[density].py }}
                      >
                        <Skeleton
                          variant="rectangular"
                          height={16}
                          sx={{ borderRadius: 1, opacity: 1 - rowIndex * 0.08 }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : null}

            {!loading && safeRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalVisibleColumns} align="center" sx={{ py: 10, borderBottom: "none" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "var(--ec-color-surface-muted, #f1f5f9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FolderOffIcon sx={{ fontSize: 32, color: "var(--ec-color-text-muted, #64748b)" }} />
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                        Nothing to show
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 360 }}>
                        {emptyMessage}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading
              ? pagedRows.map((row, rowIndex) => (
                  <TableRow
                    key={row?.id || row?._id || row?.orderNumber || `${page}-${rowIndex}`}
                    hover
                    sx={{
                      transition: "background-color 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "translateY(-1px)",
                      },
                      ...((typeof getRowSx === "function" ? getRowSx(row) : null) || {}),
                    }}
                  >
                    {safeColumns.map((column) => {
                      const columnKey = column.field || column.headerName;
                      const value = getColumnValue(row, column.field);
                      const renderedValue =
                        typeof column.renderCell === "function"
                          ? column.renderCell({ value, row })
                          : value ?? "—";

                      return (
                        <TableCell
                          key={`${columnKey}-${rowIndex}`}
                          sx={{
                            px: 2,
                            py: densitySettings[density].py,
                            fontSize: densitySettings[density].fontSize,
                            whiteSpace: column.wrap ? "normal" : "nowrap",
                            verticalAlign: "middle",
                          }}
                        >
                          {renderedValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          px: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 52,
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="body2" sx={{ color: "text.disabled", fontSize: "0.8125rem", pl: 1 }}>
          {safeRows.length > 0
            ? `Showing ${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, safeRows.length)} of ${safeRows.length}`
            : "No results"}
        </Typography>
        <TablePagination
          component="div"
          count={safeRows.length}
          page={page}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            border: 0,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "0.8125rem",
              color: "text.disabled",
              mb: 0,
            },
          }}
        />
      </Box>
    </>
  );
};

const SalesRepReport = () => {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const viewerRole = String(authState?.role || "").replace(/_/g, "-").toLowerCase();
  const selfScopedViewer = ["client-rep", "order-rep"].includes(viewerRole);
  const forcedAssignmentFilter = viewerRole === "order-rep" ? "orderRep" : "clientRep";

  const [filters, setFilters] = useState({
    ...DEFAULT_SALES_FILTERS,
    assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : DEFAULT_SALES_FILTERS.assignmentFilter,
  });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [groupedSummary, setGroupedSummary] = useState([]);
  const [repOptions, setRepOptions] = useState([]);
  const [repsLoading, setRepsLoading] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [density, setDensity] = useState("comfortable");
  const [densityMenuEl, setDensityMenuEl] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);

  const [groupPage, setGroupPage] = useState(0);
  const [groupRowsPerPage, setGroupRowsPerPage] = useState(10);
  const [groupSortBy, setGroupSortBy] = useState("");
  const [groupSortDir, setGroupSortDir] = useState("asc");
  const reportRequestSeq = useRef(0);
  const skipNextAutoLoadRef = useRef(false);

  const normalizedReportType = SALES_REPORT_TYPE_SET.has(filters.reportType) ? filters.reportType : "salesSummary";
  const columnStorageKey = `eliteCore:reports:salesRep:${normalizedReportType}:columns`;
  const selectedRepOption = useMemo(() => repOptions.find((rep) => String(rep.id) === String(filters.repId)) || null, [filters.repId, repOptions]);
  const weeklySummaryRepOption = useMemo(() => {
    if (!filters.repId) return null;
    return selectedRepOption || repOptions.find((rep) => String(rep.id) === String(filters.repId)) || null;
  }, [filters.repId, repOptions, selectedRepOption]);
  const repPickerDisabled = selfScopedViewer;
  const showWeekFilter = normalizedReportType === "weeklyPerformance";
  const showIndustryFilter = ["salesSummary", "byCustomer", "byIndustry"].includes(normalizedReportType);
  const salesViewHelperText = filters.salesView === "invoiced"
    ? "Invoiced uses paid or received invoice amounts."
    : "Booked uses approved sales order cart totals.";
  const assignmentHelperText = "Any Assignment shows all qualified sales rows unless a Rep or Team filter is selected. Client Rep Assignment and Order Rep Assignment restrict rows to those assignment roles.";

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(columnStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenColumns(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, [columnStorageKey]);

  useEffect(() => {
    if (filters.reportType === normalizedReportType) return;
    setFilters((previous) => ({ ...previous, reportType: normalizedReportType }));
  }, [filters.reportType, normalizedReportType]);

  useEffect(() => {
    if (!selfScopedViewer && filters.assignmentFilter) return;
    setFilters((previous) => ({ ...previous, assignmentFilter: forcedAssignmentFilter, repId: "" }));
  }, [filters.assignmentFilter, forcedAssignmentFilter, selfScopedViewer]);

  useEffect(() => {
    if (showIndustryFilter || !filters.industry) return;
    setFilters((previous) => ({ ...previous, industry: "" }));
  }, [filters.industry, showIndustryFilter]);

  const formatRepOptionLabel = useCallback((rep) => {
    if (!rep) return "";
    const name = repDisplayName(rep);
    return rep.email && rep.email !== name ? `${name} (${rep.email})` : name;
  }, []);

  const filteredRepOptions = useMemo(() => {
    if (filters.assignmentFilter === "clientRep") {
      return repOptions.filter((rep) => String(rep?.role || "").replace(/_/g, "-").toLowerCase() === "client-rep");
    }
    if (filters.assignmentFilter === "orderRep") {
      return repOptions.filter((rep) => String(rep?.role || "").replace(/_/g, "-").toLowerCase() === "order-rep");
    }
    return repOptions;
  }, [filters.assignmentFilter, repOptions]);

  useEffect(() => {
    if (!filters.repId) return;
    const stillEligible = filteredRepOptions.some((rep) => String(rep.id) === String(filters.repId));
    if (stillEligible) return;
    setFilters((previous) => ({ ...previous, repId: "" }));
  }, [filteredRepOptions, filters.repId]);

  const industryFilterOptions = useMemo(() => {
    const rowIndustryValues = Array.isArray(rows)
      ? rows.flatMap((row) => {
          if (Array.isArray(row?.industryValues) && row.industryValues.length) return row.industryValues;
          return row?.industry && row.industry !== UNCLASSIFIED_INDUSTRY_LABEL ? [row.industry] : [];
        })
      : [];
    const extraValues = [filters.industry, ...rowIndustryValues].filter(
      (value) => value && value !== UNCLASSIFIED_INDUSTRY_VALUE,
    );
    return [
      { value: "", label: "All Industries" },
      ...getIndustryOptions(extraValues),
      { value: UNCLASSIFIED_INDUSTRY_VALUE, label: UNCLASSIFIED_INDUSTRY_LABEL },
    ];
  }, [filters.industry, rows]);

  useEffect(() => {
    let active = true;

    const loadReps = async () => {
      setRepsLoading(true);
      try {
        const res = await fetchSalesReportReps({
          instanceUniqueId,
          assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : filters.assignmentFilter,
        });
        if (!active) return;
        setRepOptions(Array.isArray(res?.reps) ? res.reps : []);
      } catch (error) {
        console.error("Failed to load sales report reps", error);
        if (active) setRepOptions([]);
      } finally {
        if (active) setRepsLoading(false);
      }
    };

    loadReps();

    return () => {
      active = false;
    };
  }, [filters.assignmentFilter, forcedAssignmentFilter, instanceUniqueId, selfScopedViewer]);

  const formatTeamOptionLabel = useCallback((team) => {
    if (!team) return "All Teams";
    const memberCount = Number(team.memberCount ?? (Array.isArray(team.memberUserIds) ? team.memberUserIds.length : 0)) || 0;
    const memberLabel = `${memberCount} member${memberCount === 1 ? "" : "s"}`;
    return team.type ? `${team.name} — ${team.type} — ${memberLabel}` : `${team.name} — ${memberLabel}`;
  }, []);

  useEffect(() => {
    if (selfScopedViewer) {
      setTeamOptions([]);
      setTeamsLoading(false);
      return undefined;
    }

    let active = true;

    const loadTeams = async () => {
      setTeamsLoading(true);
      try {
        const res = await fetchTeams({ instanceUniqueId });
        if (!active) return;
        setTeamOptions(Array.isArray(res?.teams) ? res.teams : []);
      } catch (error) {
        console.error("Failed to load sales report teams", error);
        if (active) setTeamOptions([]);
      } finally {
        if (active) setTeamsLoading(false);
      }
    };

    loadTeams();

    return () => {
      active = false;
    };
  }, [instanceUniqueId, selfScopedViewer]);

  const load = useCallback(async (filtersOverride = null) => {
    const requestId = ++reportRequestSeq.current;
    setLoading(true);
    let params = null;
    const effectiveFilters = filtersOverride && typeof filtersOverride === "object"
      ? filtersOverride
      : filters;
    const effectiveReportType = SALES_REPORT_TYPE_SET.has(effectiveFilters?.reportType)
      ? effectiveFilters.reportType
      : "salesSummary";

    try {
      const effectiveShowIndustryFilter = effectiveReportType === "byIndustry";
      params = {
        instanceUniqueId,
        ...effectiveFilters,
        assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : effectiveFilters.assignmentFilter,
        reportType: effectiveReportType,
      };
      if (selfScopedViewer) {
        delete params.teamId;
      }
      if (effectiveReportType !== "weeklyPerformance") {
        delete params.week;
      }
      if (!effectiveShowIndustryFilter) {
        delete params.industry;
      }

      if (effectiveReportType === "weeklyPerformance" && effectiveFilters.week) {
        const [yearPart, weekPart] = String(effectiveFilters.week).split("-W");
        const year = Number(yearPart);
        const week = Number(weekPart);

        if (Number.isFinite(year) && Number.isFinite(week) && week > 0) {
          const jan4 = new Date(Date.UTC(year, 0, 4));
          const jan4Day = jan4.getUTCDay() || 7;
          const monday = new Date(jan4);
          monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);

          const sunday = new Date(monday);
          sunday.setUTCDate(monday.getUTCDate() + 6);

          params.dateFrom = monday.toISOString().slice(0, 10);
          params.dateTo = sunday.toISOString().slice(0, 10);
        }
      } else if (effectiveReportType === "weeklyPerformance") {
        if (!params.dateFrom) delete params.dateFrom;
        if (!params.dateTo) delete params.dateTo;
      }

      if (effectiveReportType === "weeklyPerformance") {
        console.info("[SalesRepReport] weekly request", {
          requestId,
          params,
        });
      }

      const res = await fetchSalesReport(params);
      if (requestId !== reportRequestSeq.current) return;

      const nextRows = Array.isArray(res?.rows) ? res.rows : [];

      if (effectiveReportType === "weeklyPerformance") {
        console.info("[SalesRepReport] weekly response", {
          requestId,
          rowCount: nextRows.length,
          summary: res?.summary,
        });
      }

      setRows(nextRows);
      setSummary(res?.summary && typeof res.summary === "object" ? res.summary : DEFAULT_SUMMARY);
      setGroupedSummary(Array.isArray(res?.groupedSummary) ? res.groupedSummary : []);
    } catch (error) {
      if (requestId !== reportRequestSeq.current) return;
      if (effectiveReportType === "weeklyPerformance") {
        console.error("[SalesRepReport] weekly failed", {
          requestId,
          error,
          status: error?.response?.status,
          response: error?.response?.data,
          params,
        });
      } else {
        console.error("Failed to load sales report", {
          error,
          response: error?.response?.data,
          status: error?.response?.status,
          params,
        });
      }
      setRows([]);
      setGroupedSummary([]);
      setSummary(DEFAULT_SUMMARY);
    } finally {
      if (requestId === reportRequestSeq.current) {
        setLoading(false);
      }
    }
  }, [filters, forcedAssignmentFilter, instanceUniqueId, selfScopedViewer]);

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [filters, rows]);

  useEffect(() => {
    setGroupPage(0);
  }, [normalizedReportType, groupedSummary]);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        setSortBy("");
        setSortDir("asc");
      }
      return;
    }

    setSortBy(columnKey);
    setSortDir("asc");
  };

  const handleGroupSort = (columnKey) => {
    if (groupSortBy === columnKey) {
      if (groupSortDir === "asc") {
        setGroupSortDir("desc");
      } else {
        setGroupSortBy("");
        setGroupSortDir("asc");
      }
      return;
    }

    setGroupSortBy(columnKey);
    setGroupSortDir("asc");
  };

  const columns = useMemo(() => {
    const salesSummaryColumns = filters.salesView === "invoiced"
      ? [
          { field: "orderDate", headerName: "Date", sortType: "date", renderCell: (p) => friendlyDate(p.value) },
          { field: "orderNumber", headerName: "Order #", sortType: "string" },
          { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
          { field: "projectTitle", headerName: "Project", minWidth: 180, wrap: true, sortType: "string" },
          {
            field: "clientRepDetails",
            headerName: "Client Rep",
            minWidth: 220,
            sortValue: ({ row }) => formatRepNames(row.clientRepDetails, row.clientRep),
            renderCell: ({ row }) => <RepList reps={row.clientRepDetails} fallback={row.clientRep} />,
          },
          {
            field: "orderRepDetails",
            headerName: "Order Rep",
            minWidth: 220,
            sortValue: ({ row }) => formatRepNames(row.orderRepDetails, row.orderRep),
            renderCell: ({ row }) => <RepList reps={row.orderRepDetails} fallback={row.orderRep} />,
          },
          {
            field: "invoicePaymentStatusLabel",
            headerName: "Payment Status",
            minWidth: 160,
            sortValue: ({ row }) => row.invoicePaymentStatus,
            renderCell: ({ row }) => (
              <UIStatusChip label={row.invoicePaymentStatusLabel || "Unknown"} tone={row.invoicePaymentStatusTone || "neutral"} />
            ),
          },
          { field: "invoicePaidAmount", headerName: "Paid Amount", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "invoiceTotalAmount", headerName: "Invoice Total", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "invoiceBalanceAmount", headerName: "Balance", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "billedCosts", headerName: "Costs", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "grossMargin", headerName: "Margin", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "grossMarginPercent", headerName: "Margin %", sortType: "percent", renderCell: (params) => percent(params.value) },
          { field: "invoicePaymentPercent", headerName: "Paid %", sortType: "percent", renderCell: (params) => percent(params.value) },
        ]
      : [
          { field: "orderDate", headerName: "Date", sortType: "date", renderCell: (p) => friendlyDate(p.value) },
          { field: "orderNumber", headerName: "Order #", sortType: "string" },
          { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
          { field: "projectTitle", headerName: "Project", minWidth: 180, wrap: true, sortType: "string" },
          {
            field: "clientRepDetails",
            headerName: "Client Rep",
            minWidth: 220,
            sortValue: ({ row }) => formatRepNames(row.clientRepDetails, row.clientRep),
            renderCell: ({ row }) => <RepList reps={row.clientRepDetails} fallback={row.clientRep} />,
          },
          {
            field: "orderRepDetails",
            headerName: "Order Rep",
            minWidth: 220,
            sortValue: ({ row }) => formatRepNames(row.orderRepDetails, row.orderRep),
            renderCell: ({ row }) => <RepList reps={row.orderRepDetails} fallback={row.orderRep} />,
          },
          { field: "invoiceTotal", headerName: "Sales", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "billedCosts", headerName: "Costs", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "grossMargin", headerName: "Margin", sortType: "currency", renderCell: (params) => currency(params.value) },
          { field: "grossMarginPercent", headerName: "Margin %", sortType: "percent", renderCell: (params) => percent(params.value) },
        ];
    const byType = {
      salesSummary: salesSummaryColumns,
      newVsRepeat: [
        { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
        { field: "firstOrderInPeriod", headerName: "First Order", sortType: "date", renderCell: (p) => friendlyDate(p.value) },
        { field: "classification", headerName: "Class", sortType: "string" },
        { field: "orderCount", headerName: "Orders", sortType: "number" },
        { field: "sales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
      ],
      byCustomer: [
        { field: "rank", headerName: "Rank", sortType: "number" },
        { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
        { field: "sales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "margin", headerName: "Margin", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "marginPct", headerName: "Margin %", sortType: "percent", renderCell: (p) => percent(p.value) },
        { field: "orderCount", headerName: "Orders", sortType: "number" },
        { field: "averageOrderSize", headerName: "Avg Order", sortType: "currency", renderCell: (p) => currency(p.value) },
      ],
      customerMarginByRep: [
        { field: "rank", headerName: "Rank", sortType: "number" },
        { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
        { field: "rep", headerName: "Rep", minWidth: 150, sortType: "string" },
        { field: "sales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "margin", headerName: "Margin", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "marginPct", headerName: "Margin %", sortType: "percent", renderCell: (p) => percent(p.value) },
      ],
      customerSalesHistory: [
        { field: "rank", headerName: "Rank", sortType: "number" },
        { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
        { field: "totalSales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "orderCount", headerName: "Orders", sortType: "number" },
        { field: "lastOrderDate", headerName: "Last Order", sortType: "date", renderCell: (p) => friendlyDate(p.value) },
      ],
      averageOrderByCustomer: [
        { field: "rank", headerName: "Rank", sortType: "number" },
        { field: "customerName", headerName: "Customer", minWidth: 180, wrap: true, sortType: "string" },
        { field: "totalSales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "orderCount", headerName: "Orders", sortType: "number" },
        { field: "averageOrderSize", headerName: "Avg Order", sortType: "currency", renderCell: (p) => currency(p.value) },
      ],
      byIndustry: [
        { field: "rank", headerName: "Rank", sortType: "number" },
        {
          field: "industry",
          headerName: "Industry",
          minWidth: 220,
          sortValue: ({ row }) => row.industryValue || row.industry,
          renderCell: ({ row, value }) => getIndustryLabel(row.industryLabel || value),
        },
        { field: "sales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "margin", headerName: "Margin", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "marginPct", headerName: "Margin %", sortType: "percent", renderCell: (p) => percent(p.value) },
        { field: "orderCount", headerName: "Orders", sortType: "number" },
        { field: "customerCount", headerName: "Customers", sortType: "number" },
        { field: "averageOrderSize", headerName: "Avg Order", sortType: "currency", renderCell: (p) => currency(p.value) },
      ],
      salesToBudget: [
        { field: "period", headerName: "Period", sortType: "string" },
        { field: "label", headerName: "Scope", minWidth: 180, wrap: true, sortType: "string" },
        { field: "actualSales", headerName: "Actual Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        {
          field: "budgetSales",
          headerName: "Budget",
          sortType: "currency",
          renderCell: (p) => (p.row?.missingBudget ? <UIStatusChip label="Missing Budget" tone="warning" /> : currency(p.value)),
        },
        { field: "varianceAmount", headerName: "Variance", sortType: "currency", renderCell: (p) => (p.value == null ? "—" : currency(p.value)) },
        { field: "variancePercent", headerName: "Variance %", sortType: "percent", renderCell: (p) => (p.value == null ? "—" : percent(p.value)) },
        { field: "percentToBudget", headerName: "% to Budget", sortType: "percent", renderCell: (p) => (p.value == null ? "—" : percent(p.value)) },
      ],
      forecast: [
        { field: "period", headerName: "Period", sortType: "string" },
        { field: "label", headerName: "Scope", minWidth: 180, wrap: true, sortType: "string" },
        { field: "actualSales", headerName: "Actual", sortType: "currency", renderCell: (p) => currency(p.value) },
        {
          field: "budgetSales",
          headerName: "Budget",
          sortType: "currency",
          renderCell: (p) => (p.row?.missingBudget ? <UIStatusChip label="Missing Budget" tone="warning" /> : currency(p.value)),
        },
        { field: "projectedSales", headerName: "Projected", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "varianceAmount", headerName: "Variance", sortType: "currency", renderCell: (p) => (p.value == null ? "—" : currency(p.value)) },
        { field: "status", headerName: "Status", sortType: "string", renderCell: (p) => renderStatusValue(p.value) },
      ],
      weeklyPerformance: [
        {
          field: "repName",
          headerName: "Rep",
          minWidth: 180,
          sortType: "string",
          renderCell: ({ value }) => {
            const normalizedValue = String(value || "").trim();
            const isUnassigned = normalizedValue.toLowerCase() === "unassigned";
            const displayValue = isUnassigned ? "Unassigned Rep" : (normalizedValue || "Unassigned Rep");

            return isUnassigned ? (
              <Tooltip title="Sales rows without an eligible client/order rep assignment for the selected assignment filter.">
                <span>{displayValue}</span>
              </Tooltip>
            ) : displayValue;
          },
        },
        { field: "totalSales", headerName: "Total Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "newBusinessSales", headerName: "New Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "repeatBusinessSales", headerName: "Repeat Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "orderCount", headerName: "Orders", sortType: "number" },
        { field: "averageOrderSize", headerName: "Avg Order", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "estimateCount", headerName: "Estimates", sortType: "number" },
        { field: "averageEstimateValue", headerName: "Avg Estimate", sortType: "currency", renderCell: (p) => currency(p.value) },
        { field: "leadCount", headerName: "Leads", sortType: "number", renderCell: (p) => (p.value == null ? "—" : p.value) },
        { field: "leadEstimateValue", headerName: "Lead Est. Value", sortType: "currency", renderCell: (p) => (p.value == null ? "—" : currency(p.value)) },
      ],
    };

    return byType[normalizedReportType] || byType.salesSummary;
  }, [filters.salesView, normalizedReportType]);

  const hasSelectedIsoWeek = normalizedReportType === "weeklyPerformance" && Boolean(String(filters.week || "").trim());
  const weeklyDateHelperText = hasSelectedIsoWeek ? "Date From and Date To are controlled by the selected week." : "";

  const groupedColumns = useMemo(
    () => [
      { field: "period", headerName: "Period", sortType: "string" },
      { field: "totalSales", headerName: "Sales", sortType: "currency", renderCell: (p) => currency(p.value) },
      { field: "totalMargin", headerName: "Margin", sortType: "currency", renderCell: (p) => currency(p.value) },
      { field: "totalOrders", headerName: "Orders", sortType: "number" },
      { field: "averageOrderSize", headerName: "Avg Order", sortType: "currency", renderCell: (p) => currency(p.value) },
      { field: "marginPct", headerName: "Margin %", sortType: "percent", renderCell: (p) => percent(p.value) },
    ],
    [],
  );

  useEffect(() => {
    const validColumnKeys = new Set(columns.map((column) => String(column.field || column.headerName || "")));
    setHiddenColumns((previous) => {
      const next = previous.filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? [] : next;
    });
  }, [columns]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(columnStorageKey, JSON.stringify(hiddenColumns));
  }, [columnStorageKey, hiddenColumns]);

  const tableRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const groupedRows = useMemo(() => (Array.isArray(groupedSummary) ? groupedSummary : []), [groupedSummary]);
  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumns.includes(String(column.field || column.headerName || ""))),
    [columns, hiddenColumns],
  );
  const sortedRows = useMemo(() => getSortedRows(tableRows, columns, sortBy, sortDir), [columns, sortBy, sortDir, tableRows]);
  const sortedGroupedRows = useMemo(
    () => getSortedRows(groupedRows, groupedColumns, groupSortBy, groupSortDir),
    [groupedColumns, groupSortBy, groupSortDir, groupedRows],
  );

  useEffect(() => {
    if (!sortBy) return;
    const visibleSortColumn = visibleColumns.some((column) => (column.field || column.headerName) === sortBy);
    if (visibleSortColumn) return;
    setSortBy("");
    setSortDir("asc");
  }, [sortBy, visibleColumns]);

  useEffect(() => {
    if (!groupSortBy) return;
    const validGroupSortColumn = groupedColumns.some((column) => (column.field || column.headerName) === groupSortBy);
    if (validGroupSortColumn) return;
    setGroupSortBy("");
    setGroupSortDir("asc");
  }, [groupSortBy, groupedColumns]);

  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: {
      ...filters,
      assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : filters.assignmentFilter,
      teamId: selfScopedViewer ? "" : filters.teamId,
      reportType: normalizedReportType,
    },
    selectedRep: normalizeSavedRepOption(selectedRepOption),
    hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
    density,
    sortBy,
    sortDir,
    groupSortBy,
    groupSortDir,
    sort: {
      field: sortBy,
      direction: sortDir,
    },
    groupedSort: {
      field: groupSortBy,
      direction: groupSortDir,
    },
    rowsPerPage,
    groupRowsPerPage,
  }), [
    density,
    filters,
    forcedAssignmentFilter,
    groupRowsPerPage,
    groupSortBy,
    groupSortDir,
    hiddenColumns,
    normalizedReportType,
    rowsPerPage,
    selectedRepOption,
    selfScopedViewer,
    sortBy,
    sortDir,
  ]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object" ? savedState.filters : {};
    const savedRep = normalizeSavedRepOption(savedState?.selectedRep)
      || normalizeSavedRepOption(savedFilters?.repId ? { id: savedFilters.repId, label: `User ${savedFilters.repId}` } : null);
    const validColumnKeys = columns.map((column) => String(column.field || column.headerName || "")).filter(Boolean);
    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys,
      fallbackHiddenColumns: [],
    });

    if (!selfScopedViewer && savedRep?.id) {
      setRepOptions((previous) => (
        previous.some((option) => String(option?.id || "") === String(savedRep.id))
          ? previous
          : [...previous, savedRep]
      ));
    }

    const nextReportType = SALES_REPORT_TYPE_SET.has(String(savedFilters?.reportType || ""))
      ? String(savedFilters.reportType)
      : "salesSummary";
    const nextGroupBy = SALES_GROUP_BY_SET.has(String(savedFilters?.groupBy || ""))
      ? String(savedFilters.groupBy)
      : "none";
    const nextSalesView = SALES_VIEW_SET.has(String(savedFilters?.salesView || ""))
      ? String(savedFilters.salesView)
      : "booked";
    const nextAssignmentFilter = selfScopedViewer
      ? forcedAssignmentFilter
      : (ASSIGNMENT_FILTER_SET.has(String(savedFilters?.assignmentFilter || ""))
        ? String(savedFilters.assignmentFilter)
        : "anyAssignment");
    const nextPeriodPreset = PERIOD_PRESET_SET.has(String(savedFilters?.periodPreset || ""))
      ? String(savedFilters.periodPreset)
      : "fiscalYear";

    const savedSortField = savedState?.sort?.field ?? savedState?.sortBy;
    const savedSortDirection = savedState?.sort?.direction ?? savedState?.sortDir;
    const savedGroupSortField = savedState?.groupedSort?.field ?? savedState?.groupSortBy;
    const savedGroupSortDirection = savedState?.groupedSort?.direction ?? savedState?.groupSortDir;

    const nextSortField = columns.some((column) => (column.field || column.headerName) === savedSortField)
      ? String(savedSortField)
      : "";
    const nextSortDirection = savedSortDirection === "desc" ? "desc" : "asc";
    const nextGroupSortField = groupedColumns.some((column) => (column.field || column.headerName) === savedGroupSortField)
      ? String(savedGroupSortField)
      : "";
    const nextGroupSortDirection = savedGroupSortDirection === "desc" ? "desc" : "asc";

    const nextFilters = {
      ...DEFAULT_SALES_FILTERS,
      ...savedFilters,
      dateFrom: String(savedFilters?.dateFrom || ""),
      dateTo: String(savedFilters?.dateTo || ""),
      week: String(savedFilters?.week || ""),
      groupBy: nextGroupBy,
      salesView: nextSalesView,
      industry: String(savedFilters?.industry || ""),
      teamId: selfScopedViewer ? "" : String(savedFilters?.teamId || ""),
      repId: selfScopedViewer ? "" : (savedRep?.id ? String(savedRep.id) : ""),
      quickFind: String(savedFilters?.quickFind || ""),
      assignmentFilter: nextAssignmentFilter,
      periodPreset: nextPeriodPreset,
      reportType: nextReportType,
    };

    setHiddenColumns(nextHiddenColumns);
    setDensity(["compact", "comfortable", "spacious"].includes(savedState?.density) ? savedState.density : "comfortable");
    setSortBy(nextSortField);
    setSortDir(nextSortDirection);
    setGroupSortBy(nextGroupSortField);
    setGroupSortDir(nextGroupSortDirection);
    setRowsPerPage([10, 25, 50, 100].includes(Number(savedState?.rowsPerPage)) ? Number(savedState.rowsPerPage) : 25);
    setGroupRowsPerPage([10, 25, 50, 100].includes(Number(savedState?.groupRowsPerPage)) ? Number(savedState.groupRowsPerPage) : 10);
    setPage(0);
    setGroupPage(0);
    skipNextAutoLoadRef.current = true;
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, forcedAssignmentFilter, groupedColumns, load, selfScopedViewer]);

  const summaryCards = useMemo(() => {
    if (normalizedReportType === "forecast") {
      return [
        ["Actual (Month)", currency(summary.currentPeriodActual)],
        ["Budget (Month)", currency(summary.currentPeriodBudget)],
        ["Projected (Month)", currency(summary.projectedPeriodSales)],
        ["Budget-Adjusted Annual Forecast", currency(summary.projectedAnnualSales)],
        ["Variance", currency(summary.varianceToBudget)],
        ["Status", summary.forecastStatus || "—"],
        ["Total Orders", summary.totalOrders ?? 0],
      ];
    }

    if (normalizedReportType === "weeklyPerformance") {
      return [
        ["Total Sales", currency(summary.totalSales)],
        ["Total Orders", summary.totalOrders ?? 0],
        ["Avg Order Size", currency(summary.averageOrderSize)],
        ["Week Start", friendlyDate(summary.weekStart)],
        ["Week End", friendlyDate(summary.weekEnd)],
        ["Activity Data", summary.activityStatusLabel || (summary.activityAvailable ? "Available" : "Pending Integration")],
        ["Rep Filter", filters.repId || "All"],
      ];
    }

    return [
      [filters.salesView === "invoiced" ? "Paid Sales" : "Total Sales", currency(summary.totalSales)],
      ...(filters.salesView === "invoiced"
        ? [
            ["Invoice Total", currency(summary.totalInvoiceAmount)],
            ["Outstanding Balance", currency(summary.totalOutstandingBalance)],
          ]
        : []),
      ["Total Margin", currency(summary.totalMargin)],
      ["Margin %", percent(summary.marginPct)],
      ["Total Orders", summary.totalOrders ?? 0],
      ["Average Order Size", currency(summary.averageOrderSize)],
      ["Month to Budget", summary.monthToBudgetPct == null ? "Missing Budget" : percent(summary.monthToBudgetPct)],
      ["Annual to Budget", summary.annualToBudgetPct == null ? "Missing Budget" : percent(summary.annualToBudgetPct)],
    ];
  }, [filters.repId, filters.salesView, normalizedReportType, summary]);

  const reportSummaryCards = useMemo(() => {
    if (normalizedReportType === "forecast") {
      return [
        { key: "forecast-actual-month", label: "Actual (Month)", value: currency(summary.currentPeriodActual), tone: "sales" },
        { key: "forecast-budget-month", label: "Budget (Month)", value: currency(summary.currentPeriodBudget), tone: "budget" },
        {
          key: "forecast-projected-month",
          label: "Projected (Month)",
          value: currency(summary.projectedPeriodSales),
          tone: "revenue",
          helperText: "Current month projection based on sales pace and recent trend.",
        },
        {
          key: "forecast-projected-annual",
          label: "Budget-Adjusted Annual Forecast",
          value: currency(summary.projectedAnnualSales),
          tone: "sales",
          helperText: "Annual budget adjusted by this month’s projected variance.",
        },
        {
          key: "forecast-variance",
          label: "Variance",
          value: currency(summary.varianceToBudget),
          tone: Number(summary.varianceToBudget) < 0 ? "danger" : "margin",
          helperText: "Projected month sales minus monthly budget.",
        },
        {
          key: "forecast-status",
          label: "Status",
          value: summary.forecastStatus || "-",
          tone: String(summary.forecastStatus || "").toLowerCase().includes("risk") ? "warning" : "percentage",
          helperText: "On track, at risk, or behind based on projected month sales versus monthly budget.",
        },
        { key: "forecast-total-orders", label: "Total Orders", value: summary.totalOrders ?? 0, tone: "count" },
      ];
    }

    if (normalizedReportType === "weeklyPerformance") {
      return [
        {
          key: "weekly-total-sales",
          label: "Total Sales",
          value: currency(summary.totalSales),
          tone: "sales",
          helperText: "Sales within the selected weekly range.",
        },
        {
          key: "weekly-total-orders",
          label: "Total Orders",
          value: summary.totalOrders ?? 0,
          tone: "count",
          helperText: "Qualified orders in the selected weekly range.",
        },
        {
          key: "weekly-average-order-size",
          label: "Avg Order Size",
          value: currency(summary.averageOrderSize),
          tone: "revenue",
          helperText: "Total sales divided by order count.",
        },
        {
          key: "weekly-week-start",
          label: "Week Start",
          value: friendlyDate(summary.weekStart),
          tone: "count",
          helperText: "Monday start of the selected ISO week.",
        },
        {
          key: "weekly-week-end",
          label: "Week End",
          value: friendlyDate(summary.weekEnd),
          tone: "count",
          helperText: "Sunday end of the selected ISO week.",
        },
        {
          key: "weekly-activity-data",
          label: "Activity Data",
          value: summary.activityStatusLabel || (summary.activityAvailable ? "Available" : "Pending Integration"),
          tone: summary.activityAvailable ? "margin" : "warning",
          helperText: summary.activityAvailable
            ? "Sales order and customer-created activity is available. Email/customer-touch activity requires a future integration."
            : "Customer activity metrics require a future integration and are not available yet.",
        },
        {
          key: "weekly-rep-filter",
          label: "Rep Filter",
          value: filters.repId
            ? <RepSummaryValue rep={weeklySummaryRepOption} fallback={`User ${filters.repId}`} />
            : "All",
          tone: "count",
          helperText: filters.repId
            ? "Selected rep scope."
            : "Selected rep scope, or All when no rep is selected.",
        },
      ];
    }

    return [
      {
        key: "total-sales",
        label: filters.salesView === "invoiced" ? "Paid Sales" : "Total Sales",
        value: currency(summary.totalSales),
        tone: "sales",
        helperText: filters.salesView === "invoiced" ? "Paid and received invoice value." : "Approved sales order cart totals.",
      },
      ...(filters.salesView === "invoiced"
        ? [
            { key: "invoice-total", label: "Invoice Total", value: currency(summary.totalInvoiceAmount), tone: "revenue" },
            { key: "outstanding-balance", label: "Outstanding Balance", value: currency(summary.totalOutstandingBalance), tone: Number(summary.totalOutstandingBalance) > 0 ? "warning" : "count" },
          ]
        : []),
      { key: "total-margin", label: "Total Margin", value: currency(summary.totalMargin), tone: Number(summary.totalMargin) < 0 ? "danger" : "margin" },
      { key: "margin-pct", label: "Margin %", value: percent(summary.marginPct), tone: Number(summary.marginPct) < 0 ? "danger" : "percentage" },
      { key: "total-orders", label: "Total Orders", value: summary.totalOrders ?? 0, tone: "count" },
      { key: "average-order-size", label: "Average Order Size", value: currency(summary.averageOrderSize), tone: "sales" },
      {
        key: "month-to-budget",
        label: "Month to Budget",
        value: summary.monthToBudgetPct == null ? "Missing Budget" : percent(summary.monthToBudgetPct),
        tone: summary.monthToBudgetPct == null ? "warning" : "budget",
        helperText: summary.monthToBudgetPct == null ? "No company or selected rep budget found." : "Filtered sales versus the resolved monthly budget.",
      },
      {
        key: "annual-to-budget",
        label: "Annual to Budget",
        value: summary.annualToBudgetPct == null ? "Missing Budget" : percent(summary.annualToBudgetPct),
        tone: summary.annualToBudgetPct == null ? "warning" : "budget",
        helperText: summary.annualToBudgetPct == null ? "No company or selected rep budget found." : "Filtered fiscal-year sales versus budget.",
      },
    ];
  }, [filters.repId, filters.salesView, normalizedReportType, summary]);

  const onExport = async () => {
    const response = await exportSalesCsv({
      instanceUniqueId,
      ...filters,
      assignmentFilter: selfScopedViewer ? forcedAssignmentFilter : filters.assignmentFilter,
      teamId: selfScopedViewer ? "" : filters.teamId,
      reportType: normalizedReportType,
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sales-rep-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />

      <div className="rightSide">
        <Box
          className="fade-in-effect-usability"
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            width: "100%",
          }}
        >
            <UITableShell
              customLayout
              paperSx={reportShellSx}
              toolbar={
                <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                        Sales Rep Report
                      </Typography>

                      <Chip
                        label={`${tableRows.length} total`}
                        size="small"
                        sx={{
                          backgroundColor: "var(--ec-status-neutral-bg)",
                          color: "var(--ec-status-neutral-text)",
                          border: "1px solid var(--ec-status-neutral-border)",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                        }}
                      />

                      <Chip
                        label={normalizedReportType}
                        size="small"
                        sx={{
                          backgroundColor: "var(--ec-status-info-bg)",
                          color: "var(--ec-status-info-text)",
                          border: "1px solid var(--ec-status-info-border)",
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          height: 22,
                          textTransform: "none",
                        }}
                      />
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ReportSavedReportsButton
                        instanceUniqueId={instanceUniqueId}
                        reportKey="sales"
                        currentState={currentSavedReportState}
                        onApplyReport={applySavedReportState}
                      />
                      <ReportColumnVisibilityButton
                        columns={columns}
                        hiddenColumns={hiddenColumns}
                        onHiddenColumnsChange={setHiddenColumns}
                      />
                      <Tooltip title="Row density">
                        <IconButton
                          size="small"
                          onClick={(event) => setDensityMenuEl(event.currentTarget)}
                          sx={{ color: "text.secondary" }}
                        >
                          <SettingsIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={densityMenuEl}
                        open={Boolean(densityMenuEl)}
                        onClose={() => setDensityMenuEl(null)}
                        slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ px: 2, pt: 1.25, pb: 0.5, display: "block", color: "text.disabled" }}
                        >
                          Row density
                        </Typography>
                        {["compact", "comfortable", "spacious"].map((option) => (
                          <MenuItem
                            key={option}
                            onClick={() => {
                              setDensity(option);
                              setDensityMenuEl(null);
                            }}
                            selected={density === option}
                          >
                            <Typography variant="body2" sx={{ textTransform: "capitalize", fontWeight: 500 }}>
                              {option}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Menu>

                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />

                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                        onClick={onExport}
                        sx={{
                          fontWeight: 700,
                          textTransform: "none",
                          height: 34,
                          px: 2,
                          boxShadow: "none",
                          "&:hover": { boxShadow: "none" },
                        }}
                      >
                        Export CSV
                      </Button>
                    </Stack>
                  </Stack>
                </UITableToolbar>
              }
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 1, md: 1.5 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.25, md: 1.5 },
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={1.5}
                    sx={{ mb: 1.5 }}
                  >
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: "0.08em" }}
                      >
                        Report Filters
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Sales Rep Report Controls
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Filter booked or invoiced sales performance by period, assignment, team, rep, and industry.
                      </Typography>
                    </Box>

                    <UIStatusChip label={`${filters.groupBy || "none"} grouping`} tone="accent" />
                  </Stack>

                  <Box sx={{ ...reportFilterCardContentSx, ...reportFilterGridSx }}>
                    <TextField
                      label="Date From"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters((previous) => ({ ...previous, dateFrom: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      disabled={hasSelectedIsoWeek}
                      helperText={weeklyDateHelperText}
                    />
                    <TextField
                      label="Date To"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters((previous) => ({ ...previous, dateTo: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      disabled={hasSelectedIsoWeek}
                      helperText={weeklyDateHelperText}
                    />
                    <TextField
                      select
                      label="Period"
                      value={filters.periodPreset}
                      onChange={(e) => setFilters((previous) => ({ ...previous, periodPreset: e.target.value }))}
                      SelectProps={{ native: true }}
                      size="small"
                    >
                      <option value="fiscalYear">Fiscal Year</option>
                      <option value="calendarYear">Calendar Year</option>
                      <option value="custom">Custom Range</option>
                    </TextField>
                    {!selfScopedViewer ? (
                      <TextField
                        select
                        label="Assignment Filter"
                        value={filters.assignmentFilter}
                        onChange={(e) => setFilters((previous) => ({ ...previous, assignmentFilter: e.target.value }))}
                        SelectProps={{ native: true }}
                        size="small"
                        helperText={assignmentHelperText}
                      >
                        {ASSIGNMENT_FILTER_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    ) : null}
                    <TextField
                      select
                      label="Report Type"
                      value={normalizedReportType}
                      onChange={(e) => setFilters((previous) => ({ ...previous, reportType: e.target.value }))}
                      SelectProps={{ native: true }}
                      size="small"
                    >
                      {SALES_REPORT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </TextField>
                    {showWeekFilter ? (
                      <TextField
                        label="Week (ISO)"
                        placeholder="YYYY-Www"
                        value={filters.week}
                        onChange={(e) => setFilters((previous) => ({ ...previous, week: e.target.value }))}
                        size="small"
                        helperText="Selecting an ISO week sets the report range from Monday to Sunday."
                      />
                    ) : null}
                    {normalizedReportType !== "weeklyPerformance" ? (
                      <TextField
                        select
                        label="Group By"
                        value={filters.groupBy}
                        onChange={(e) => setFilters((previous) => ({ ...previous, groupBy: e.target.value }))}
                        SelectProps={{ native: true }}
                        size="small"
                      >
                        <option value="none">None</option>
                        <option value="month">Month</option>
                        <option value="quarter">Quarter</option>
                        <option value="annual">Annual</option>
                      </TextField>
                    ) : null}
                    <TextField
                      select
                      label="Sales View"
                      value={filters.salesView}
                      onChange={(e) => setFilters((previous) => ({ ...previous, salesView: e.target.value }))}
                      SelectProps={{ native: true }}
                      size="small"
                      helperText={salesViewHelperText}
                    >
                      <option value="booked">Booked</option>
                      <option value="invoiced">Invoiced</option>
                    </TextField>
                    {!selfScopedViewer ? (
                      <TextField
                        select
                        label="Team"
                        value={filters.teamId}
                        onChange={(e) => setFilters((previous) => ({ ...previous, teamId: e.target.value }))}
                        SelectProps={{ native: true }}
                        size="small"
                        disabled={teamsLoading || teamOptions.length === 0}
                        helperText={teamsLoading ? "Loading teams..." : teamOptions.length === 0 ? "No teams found" : ""}
                      >
                        <option value="">{teamsLoading ? "Loading teams..." : "All Teams"}</option>
                        {!teamsLoading && teamOptions.length === 0 ? <option value="" disabled>No teams found</option> : null}
                        {teamOptions.map((team) => (
                          <option key={team.id} value={team.id}>
                            {formatTeamOptionLabel(team)}
                          </option>
                        ))}
                      </TextField>
                    ) : null}
                    {showIndustryFilter ? (
                      <TextField
                        select
                        label="Industry"
                        value={filters.industry}
                        onChange={(e) => setFilters((previous) => ({ ...previous, industry: e.target.value }))}
                        SelectProps={{ native: true }}
                        size="small"
                      >
                        {industryFilterOptions.map((option) => (
                          <option key={option.value || "__all_industries__"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </TextField>
                    ) : null}
                    {!repPickerDisabled ? (
                      <Autocomplete
                        options={filteredRepOptions}
                        value={selectedRepOption}
                        onChange={(_, option) => setFilters((previous) => ({ ...previous, repId: option?.id ? String(option.id) : "" }))}
                        getOptionLabel={formatRepOptionLabel}
                        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                        loading={repsLoading}
                        disabled={repsLoading || filteredRepOptions.length === 0}
                        noOptionsText={repsLoading ? "Loading reps..." : "No eligible reps"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Rep Filter"
                            placeholder="All eligible reps"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {repsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : null}
                    <TextField
                      label="Quick Find"
                      value={filters.quickFind}
                      onChange={(e) => setFilters((previous) => ({ ...previous, quickFind: e.target.value }))}
                      size="small"
                    />
                  </Box>
                </Paper>

                <Box sx={reportSummaryGridSx}>
                  {reportSummaryCards.map((card) => (
                    <ReportSummaryCard
                      key={card.key || card.label}
                      label={card.label}
                      value={card.value}
                      tone={card.tone}
                      helperText={card.helperText}
                      status={card.status}
                      sx={card.sx}
                    />
                  ))}
                </Box>

                {normalizedReportType === "weeklyPerformance" && !summary.activityAvailable ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "var(--ec-status-warning-bg)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Activity metrics placeholder: customer activity tracking (emails per customer/week) requires a future integration and is not available yet.
                    </Typography>
                  </Paper>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: { xs: 420, md: 600 },
                    overflow: "hidden",
                  }}
                >
                  <ReportTableContent
                    rows={sortedRows}
                    columns={visibleColumns}
                    loading={loading}
                    emptyMessage="No sales rep data yet for selected filters."
                    page={page}
                    rowsPerPage={rowsPerPage}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                    onPageChange={setPage}
                    onRowsPerPageChange={(nextRowsPerPage) => {
                      setRowsPerPage(nextRowsPerPage);
                      setPage(0);
                    }}
                    density={density}
                    getRowSx={filters.reportType === "salesSummary" && filters.salesView === "invoiced" ? getInvoicePaymentRowAccentSx : undefined}
                  />
                </Box>
              </Box>
            </UITableShell>

            {groupedRows.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <UITableShell
                  customLayout
                  paperSx={reportShellSx}
                  toolbar={
                    <UITableToolbar sx={{ backgroundColor: "background.paper" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Grouped Summary
                          </Typography>
                          <Chip
                            label={`${groupedRows.length} total`}
                            size="small"
                            sx={{
                              backgroundColor: "var(--ec-status-neutral-bg)",
                              color: "var(--ec-status-neutral-text)",
                              border: "1px solid var(--ec-status-neutral-border)",
                              fontWeight: 600,
                              fontSize: "0.72rem",
                              height: 22,
                            }}
                          />
                        </Stack>
                      </Stack>
                    </UITableToolbar>
                  }
                >
                  <ReportTableContent
                    rows={sortedGroupedRows}
                    columns={groupedColumns}
                    loading={false}
                    emptyMessage="No grouped summary data."
                    page={groupPage}
                    rowsPerPage={groupRowsPerPage}
                    sortBy={groupSortBy}
                    sortDir={groupSortDir}
                    onSort={handleGroupSort}
                    onPageChange={setGroupPage}
                    onRowsPerPageChange={(nextRowsPerPage) => {
                      setGroupRowsPerPage(nextRowsPerPage);
                      setGroupPage(0);
                    }}
                    density={density}
                  />
                </UITableShell>
              </Box>
            ) : null}
        </Box>
      </div>
    </div>
  );
};

export default SalesRepReport;
